<template>
  <div class="overflow-x-clip bg-neutral-50">
    <Head v-if="service" :title="service.name" />

    <div v-if="service" class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="servicesIndexHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.services') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#2ea8cf]">{{ service.name }}</span>
      </nav>

      <div class="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <!-- Left column: content cards -->
        <div class="flex min-w-0 flex-col gap-6">
          <!-- 1. Service header card: category badge + name -->
          <article
            id="usluga"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <div v-if="service.category_label" class="flex min-w-0 flex-wrap items-center gap-2">
              <neutral-pill :label="service.category_label" />
            </div>

            <h1 class="min-w-0 break-words text-xl font-medium leading-7 tracking-[-0.02em] text-neutral-900">
              {{ service.name }}
            </h1>
          </article>

          <button
            v-if="hasBookableSpecialists"
            type="button"
            class="flex h-12 w-full items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-opacity hover:opacity-90 lg:hidden"
            :style="{ backgroundImage: brandGradient }"
            @click="openBookingModal"
          >
            {{ bookButtonLabel }}
          </button>

          <!-- 2. O badaniu / konsultacji / zabiegu: description + important information -->
          <section
            v-if="hasAboutSection"
            id="o-usludze"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ aboutHeading }}
            </h2>

            <ExpandableText
              v-if="service.description"
              :text="service.description"
            />

            <template v-if="service.important_information?.length">
              <hr class="border-t border-dashed border-neutral-200">
              <h3 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
                {{ $t('services.important_information') }}
              </h3>
              <ul class="flex flex-col gap-3">
                <li
                  v-for="(info, index) in service.important_information"
                  :key="index"
                  class="flex min-w-0 gap-2 text-base leading-6 text-neutral-900"
                >
                  <Icon
                    name="warning"
                    class="mt-0.5 size-5 shrink-0 text-amber-500"
                    aria-hidden="true"
                  />
                  <span class="min-w-0 break-words whitespace-pre-line">{{ info }}</span>
                </li>
              </ul>
            </template>
          </section>

          <!-- 3. Lekarze wykonujący badanie / konsultację / zabieg -->
          <section
            v-if="serviceSpecialists.length"
            id="lekarze"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ doctorsHeading }}
            </h2>
            <ul class="flex min-w-0 flex-col divide-y divide-neutral-200">
              <li
                v-for="specialist in serviceSpecialists"
                :key="specialist.id"
                class="flex min-w-0 flex-col gap-3 pt-4 first:pt-0 last:pb-0 lg:flex-row lg:flex-wrap lg:items-center"
              >
                <SpecialistCard :specialist="specialist" flat class="min-w-0 flex-1" />

                <div
                  v-if="isBookable(specialist) && isSelectedSpecialist(specialist)"
                  class="flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-sky-700 lg:w-auto"
                >
                  <Icon name="check" class="size-5 shrink-0" aria-hidden="true" />
                  <span>{{ $t('services.selected_doctor') }}</span>
                </div>

                <button
                  v-else-if="isBookable(specialist)"
                  type="button"
                  data-doctor-select
                  class="flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-opacity hover:opacity-90 lg:w-auto"
                  :style="{ backgroundImage: brandGradient }"
                  @click="selectSpecialist(specialist)"
                >
                  {{ bookButtonLabel }}
                </button>
              </li>
            </ul>
          </section>

          <!-- 4. FAQ -->
          <section
            v-if="service.faq?.length"
            id="faq"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('faq.title') }}
            </h2>
            <div class="flex min-w-0 flex-col gap-3">
              <details
                v-for="(item, index) in service.faq"
                :key="index"
                class="group min-w-0 rounded-lg border border-neutral-200 bg-white p-4"
              >
                <summary
                  class="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-medium leading-6 text-neutral-900"
                >
                  <span class="min-w-0 break-words">{{ item.question }}</span>
                  <svg
                    class="size-5 shrink-0 text-neutral-500 transition-transform group-open:rotate-180"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 7.5 10 12.5 15 7.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </summary>
                <p
                  v-if="item.answer"
                  class="mt-4 break-words border-t border-neutral-100 pt-4 text-base leading-6 text-neutral-700 whitespace-pre-line"
                >
                  {{ item.answer }}
                </p>
              </details>
            </div>
          </section>
        </div>

        <!-- Right column: booking panel with specialist selector (desktop only) -->
        <aside
          id="umow-wizyte"
          class="hidden min-w-0 scroll-mt-[48px] lg:block lg:sticky lg:self-start lg:scroll-mt-[64px] transition-[top] duration-300 ease-out"
          :style="{ top: `${stickyPanelTop}px` }"
        >
          <ServiceSlotsCalendar
            ref="calendar"
            :service="service"
            :title="bookingHeading"
            @focus-change="onCalendarFocusChange"
          />
        </aside>
      </div>

      <BookingSlideOver
        :open="bookingModalOpen"
        :title="bookingHeading"
        :subtitle="service.name"
        @close="closeBookingModal"
      >
        <ServiceSlotsCalendar
          v-if="bookingModalOpen"
          ref="mobileCalendar"
          :service="service"
          :title="bookingHeading"
          :show-header="false"
          flush
          @focus-change="onCalendarFocusChange"
        />
      </BookingSlideOver>
    </div>

    <div v-else class="mx-auto max-w-[1200px] px-4 py-16 text-center text-neutral-500">
      {{ $t('services.not_found') }}
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import NeutralPill from '@/Shared/NeutralPill.vue';
import ExpandableText from '@/Shared/ExpandableText.vue';
import Icon from '@/Shared/Icon.vue';
import SpecialistCard from '@/Shared/SpecialistCard.vue';
import ServiceSlotsCalendar from '@/Shared/ServiceSlotsCalendar.vue';
import BookingSlideOver from '@/Shared/BookingSlideOver.vue';
import { stickyPanelTop } from '@/utils/stickyMainHeader';
import { BRAND_GRADIENT } from '@/utils/siteThemeColors';

// Below lg, booking opens in a slide-over modal instead of an inline panel.
const LG_BREAKPOINT = 1024;

export default {
  name: 'ServiceShow',
  components: { Head, Link, NeutralPill, ExpandableText, Icon, SpecialistCard, ServiceSlotsCalendar, BookingSlideOver },
  props: {
    service: { type: Object, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  data() {
    return {
      bookingModalOpen: false,
      brandGradient: BRAND_GRADIENT,
      // Slug of the specialist currently focused in ServiceSlotsCalendar, or null.
      selectedSpecialistSlug: null,
    };
  },
  computed: {
    stickyPanelTop() {
      return stickyPanelTop.value;
    },
    homeHref() {
      const ctx = this.site_context;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    servicesIndexHref() {
      return this.$routes.services_index();
    },
    categoryLabelKey() {
      const category = this.service?.category;
      return ['badanie', 'konsultacja_medyczna', 'zabieg'].includes(category)
        ? category
        : 'default';
    },
    aboutHeading() {
      return this.$t(`services.about_heading.${this.categoryLabelKey}`);
    },
    doctorsHeading() {
      return this.$t(`services.doctors_heading.${this.categoryLabelKey}`);
    },
    bookButtonLabel() {
      return this.$t(`services.book_button.${this.categoryLabelKey}`);
    },
    bookingHeading() {
      return this.$t(`services.book_button.${this.categoryLabelKey}`);
    },
    hasAboutSection() {
      return Boolean(
        this.service?.description
        || this.service?.important_information?.length,
      );
    },
    serviceSpecialists() {
      return this.service?.specialists || [];
    },
    hasBookableSpecialists() {
      return this.serviceSpecialists.some((specialist) => this.isBookable(specialist));
    },
  },
  methods: {
    isBookable(specialist) {
      return Boolean(specialist?.bookable_services?.length);
    },
    isSelectedSpecialist(specialist) {
      return Boolean(specialist?.slug) && specialist.slug === this.selectedSpecialistSlug;
    },
    onCalendarFocusChange(slug) {
      this.selectedSpecialistSlug = slug || null;
    },
    openBookingModal() {
      this.selectedSpecialistSlug = null;
      this.bookingModalOpen = true;
    },
    closeBookingModal() {
      this.bookingModalOpen = false;
      this.selectedSpecialistSlug = null;
    },
    selectSpecialist(specialist) {
      this.selectedSpecialistSlug = specialist.slug;

      if (window.innerWidth < LG_BREAKPOINT) {
        this.bookingModalOpen = true;
        this.$nextTick(() => {
          this.$refs.mobileCalendar?.focusDoctor(specialist.slug);
        });
        return;
      }

      this.$refs.calendar?.focusDoctor(specialist.slug);
    },
  },
};
</script>
